import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import auditLogService from '../../services/auditLogService';
import { 
  FileText, 
  RefreshCw, 
  Filter, 
  ChevronLeft, 
  ChevronRight,
  User,
  Clock,
  Globe,
  Activity,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  Eye
} from 'lucide-react';
import './LogsPage.css';

const LogsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    totalPages: 1,
  });
  const [filters, setFilters] = useState({
    appLabel: '',
    model: '',
    objectId: '',
    action: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  const actionColors = {
    CREATE: '#22c55e',
    UPDATE: '#3b82f6',
    DELETE: '#ef4444',
    LOGIN: '#10b981',
    LOGOUT: '#6b7280',
    LOGIN_FAILED: '#f59e0b',
  };

  const actionLabels = {
    CREATE: 'Création',
    UPDATE: 'Modification',
    DELETE: 'Suppression',
    LOGIN: 'Connexion',
    LOGOUT: 'Déconnexion',
    LOGIN_FAILED: 'Échec connexion',
  };

  const fetchLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await auditLogService.getObjectHistory({
        appLabel: filters.appLabel || undefined,
        model: filters.model || undefined,
        objectId: filters.objectId || undefined,
        page: page,
        pageSize: 20,
      });

      setLogs(response.results || []);
      
      // Calculer la pagination
      const count = response.count || 0;
      const totalPages = Math.ceil(count / 20);
      
      setPagination({
        count: count,
        next: response.next,
        previous: response.previous,
        currentPage: page,
        totalPages: totalPages,
      });
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchLogs(newPage);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyFilters = () => {
    fetchLogs(1);
  };

  const clearFilters = () => {
    setFilters({
      appLabel: '',
      model: '',
      objectId: '',
      action: '',
    });
    setTimeout(() => fetchLogs(1), 0);
  };

  const toggleExpandLog = (logId) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const renderChanges = (changes) => {
    if (!changes) return <span className="no-changes">Aucun détail</span>;
    
    try {
      return (
        <pre className="changes-json">
          {JSON.stringify(changes, null, 2)}
        </pre>
      );
    } catch (e) {
      return <span className="no-changes">Impossible d'afficher les changements</span>;
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>Chargement des logs...</span>
      </div>
    );
  }

  return (
    <div className="logs-page">
      <div className="page-header-section">
        <div className="header-titles">
          <h1 className="page-title">
            <FileText size={32} />
            Journal d'Audit
          </h1>
          <p className="page-subtitle">
            Historique des actions des utilisateurs
          </p>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => setShowFilters(!showFilters)} 
            className={`btn-secondary ${showFilters ? 'active' : ''}`}
          >
            <Filter size={20} />
            Filtres
          </button>
          <button 
            onClick={() => fetchLogs(pagination.currentPage)} 
            className="btn-secondary"
            disabled={loading}
          >
            <RefreshCw size={20} className={loading ? 'spinning' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filters-row">
            <div className="filter-group">
              <label>Application</label>
              <select 
                value={filters.appLabel} 
                onChange={(e) => handleFilterChange('appLabel', e.target.value)}
              >
                <option value="">Toutes</option>
                <option value="sessions_app">Sessions</option>
                <option value="users">Utilisateurs</option>
                <option value="catalog">Formations</option>
                <option value="suppliers">Fournisseurs</option>
                <option value="centers">Centres</option>
                <option value="notifications">Notifications</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Modèle</label>
              <input 
                type="text" 
                value={filters.model}
                onChange={(e) => handleFilterChange('model', e.target.value)}
                placeholder="Ex: sessionformation"
              />
            </div>
            <div className="filter-group">
              <label>ID Objet</label>
              <input 
                type="text" 
                value={filters.objectId}
                onChange={(e) => handleFilterChange('objectId', e.target.value)}
                placeholder="UUID de l'objet"
              />
            </div>
          </div>
          <div className="filters-actions">
            <button onClick={applyFilters} className="btn-primary">
              <Search size={18} />
              Appliquer
            </button>
            <button onClick={clearFilters} className="btn-secondary">
              <X size={18} />
              Réinitialiser
            </button>
          </div>
        </div>
      )}

      <div className="logs-stats">
        <div className="stat-card">
          <Activity size={24} />
          <div>
            <span className="stat-value">{pagination.count}</span>
            <span className="stat-label">Total logs</span>
          </div>
        </div>
      </div>

      <div className="logs-table-container">
        <table className="logs-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Action</th>
              <th>Utilisateur</th>
              <th>Type</th>
              <th>ID Objet</th>
              <th>IP</th>
              <th>Détails</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <React.Fragment key={log.id}>
                <tr className={`log-row ${expandedLog === log.id ? 'expanded' : ''}`}>
                  <td className="timestamp-cell" data-label="Date">
                    <Clock size={14} />
                    {formatDate(log.timestamp)}
                  </td>
                  <td data-label="Action">
                    <span 
                      className="action-badge"
                      style={{ 
                        backgroundColor: `${actionColors[log.action] || '#6b7280'}20`,
                        color: actionColors[log.action] || '#6b7280',
                        borderColor: actionColors[log.action] || '#6b7280'
                      }}
                    >
                      {actionLabels[log.action] || log.action}
                    </span>
                  </td>
                  <td className="user-cell" data-label="Utilisateur">
                    <User size={14} />
                    {log.actor_data ? `${log.actor_data.first_name} ${log.actor_data.last_name}` : 'Système'}
                  </td>
                  <td data-label="Type">
                    {log.object_type ? `${log.object_type.app_label}.${log.object_type.model}` : '-'}
                  </td>
                  <td className="object-id-cell" data-label="ID Objet">
                    {log.object_id ? log.object_id.substring(0, 8) + '...' : '-'}
                  </td>
                  <td className="ip-cell" data-label="IP">
                    <Globe size={14} />
                    {log.ip_address || '-'}
                  </td>
                  <td data-label="Détails">
                    <button 
                      className="expand-btn"
                      onClick={() => toggleExpandLog(log.id)}
                    >
                      <Eye size={16} />
                      {expandedLog === log.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </td>
                </tr>
                {expandedLog === log.id && (
                  <tr className="details-row">
                    <td colSpan="7">
                      <div className="log-details">
                        <div className="detail-section">
                          <h4>Informations</h4>
                          <div className="detail-grid">
                            <div><strong>Path:</strong> {log.path || '-'}</div>
                            <div><strong>Méthode:</strong> {log.method || '-'}</div>
                            <div><strong>User Agent:</strong> {log.user_agent || '-'}</div>
                            <div><strong>Session:</strong> {log.session_key ? log.session_key.substring(0, 16) + '...' : '-'}</div>
                          </div>
                        </div>
                        {log.changes && (
                          <div className="detail-section">
                            <h4>Changements</h4>
                            {renderChanges(log.changes)}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {logs.length === 0 && !loading && (
        <div className="logs-empty">
          <FileText size={48} />
          <p>Aucun log trouvé</p>
        </div>
      )}

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.previous || loading}
            className="pagination-btn"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="pagination-info">
            Page {pagination.currentPage} / {pagination.totalPages}
          </span>
          <button 
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.next || loading}
            className="pagination-btn"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default LogsPage;
